import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Contact, ContactEntityKind, EmailLijstKey, User } from "@domaddress/shared";
import {
  CONTACT_RELATION_LABELS_NL,
  canDuplicateContactCard,
  contactIsDeceased,
  isPartnerLifecycleRelationCode,
  PARTNER_LIFECYCLE_CODES,
  type ContactRelationRow,
  type PartnerLifecycleRelationCode,
} from "@domaddress/shared";
import { api } from "../lib/api";
import {
  summarizeContactAuditRow,
  formatAuditWhen,
  type ContactAuditTimelineRow,
} from "../lib/contactAuditTimelineNl";
import ContactRelationAddModal from "./ContactRelationAddModal";
import { CopyTextButton, MapsLinkButton, formatLocationMapsQuery } from "./DetailFieldActions";
import DeceasedMark from "./DeceasedMark";

/** De vijf officiële lijsten die een contact als primaire of secundaire lijst kan hebben */
const OFFICIËLE_LIJSTEN: { value: ContactEntityKind; label: string }[] = [
  { value: "personeel",       label: "Personeel" },
  { value: "vrijwilliger",    label: "Vrijwilliger" },
  { value: "dominicusganger", label: "Dominicusganger" },
  { value: "extern",          label: "Extern contact" },
  { value: "leverancier",     label: "Leverancier" },
  { value: "emaillijst",      label: "E-mailabonnee" },
];

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

const emailLijstLabel: Record<EmailLijstKey, string> = {
  geen: "—",
  alle_email: "Allen met e-mail",
  informatief: "Informatieve berichten",
  overlijden: "Bericht bij overlijden",
  "40_dagen": "40-dagen-mailing",
};

type Props = {
  contact: Contact;
  onClose: () => void;
  /** Wordt aangeroepen met de getoonde (eventueel verfriste) kaart; erna sluit de parent het venster. */
  onEdit?: (c: Contact) => void;
  /** Voor o.a. GET om groepen/velden up-to-date te houden */
  apiPath: string;
  title: string;
  /** Standaard aan; bij openen vanuit groepspagina zonder bewerk-flow uit. */
  showEditButton?: boolean;
  /** Ingelogde gebruiker: o.a. “Dupliceren en bewerken” voor secretaris/beheerder/super. */
  user?: User | null;
};

function Row({
  label,
  value,
  copyText,
  mapsQuery,
}: {
  label: string;
  value: ReactNode;
  /** Platte tekst om te kopiëren (los van eventuele links in `value`). */
  copyText?: string | null;
  /** Volledige adresregel voor Google Maps (één knop per logische locatie). */
  mapsQuery?: string | null;
}) {
  if (value == null || value === "") return null;
  const copy = (copyText ?? "").trim();
  const maps = (mapsQuery ?? "").trim();
  const showActions = Boolean(copy || maps);
  return (
    <div className="contact-view-row">
      <div className="contact-view-lbl">{label}</div>
      <div className={`contact-view-val${showActions ? " contact-view-val--with-actions" : ""}`}>
        <span className="contact-view-val-main">{value}</span>
        {showActions && (
          <span className="detail-inline-actions" onClick={(e) => e.stopPropagation()}>
            {copy ? <CopyTextButton text={copy} label={label} /> : null}
            {maps ? <MapsLinkButton query={maps} /> : null}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Alleen-lezen overzicht van één medewerker- of contactkaart; opent vanaf klik op kaart/rij.
 */
export default function ContactViewModal({
  contact,
  onClose,
  onEdit,
  apiPath,
  title,
  showEditButton = true,
  user = null,
}: Props) {
  const [data, setData] = useState<Contact | null>(contact);
  const [err, setErr] = useState("");
  const [duplicateBusy, setDuplicateBusy] = useState(false);

  // ── Labels ──────────────────────────────────────────────────────────────────────────────
  const [labels, setLabels] = useState<string[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelSuggestions, setLabelSuggestions] = useState<string[]>([]);
  const newLabelRef = useRef<HTMLInputElement>(null);

  /** Tijdlijn: `ready` = data geladen; `forbidden` = geen rechten (sectie wordt niet getoond); `error` = netwerk/server. */
  const [contactAuditRows, setContactAuditRows] = useState<ContactAuditTimelineRow[]>([]);
  const [contactAuditPanel, setContactAuditPanel] = useState<"loading" | "ready" | "forbidden" | "error">("loading");

  const [relations, setRelations] = useState<
    | { status: "loading" }
    | { status: "ready"; editable: boolean; outgoing: ContactRelationRow[]; incoming: ContactRelationRow[] }
    | { status: "hidden" }
  >({ status: "loading" });
  const [relationModalOpen, setRelationModalOpen] = useState(false);
  const [relationPatchBusy, setRelationPatchBusy] = useState(false);
  const [mlStatus, setMlStatus] = useState<{ enabled: boolean; canReview: boolean } | null>(null);
  const [mlSyncBusy, setMlSyncBusy] = useState(false);
  const [mlSyncMsg, setMlSyncMsg] = useState("");
  /** Relaties-kaart in de zijbalk: scroll hiernaartoe bij klik op een naam. */
  const relationsSectionRef = useRef<HTMLDivElement>(null);
  const scrollToRelationsSection = useCallback(() => {
    relationsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const reloadRelations = useCallback(async () => {
    try {
      const res = await api.get<{
        data: { editable: boolean; outgoing: ContactRelationRow[]; incoming: ContactRelationRow[] };
      }>(`${apiPath}/${contact.id}/relations`);
      setRelations({
        status: "ready",
        editable: res.data.data.editable,
        outgoing: res.data.data.outgoing ?? [],
        incoming: res.data.data.incoming ?? [],
      });
    } catch {
      setRelations({ status: "hidden" });
    }
  }, [apiPath, contact.id]);

  const loadLabels = async () => {
    try {
      const res = await api.get<{ data: string[] }>(`${apiPath}/${contact.id}/labels`);
      setLabels(res.data.data);
    } catch {
      /* stille fout */
    }
  };

  const loadLabelSuggestions = async () => {
    try {
      const res = await api.get<{ data: string[] }>("/api/labels");
      setLabelSuggestions(res.data.data ?? []);
    } catch {
      setLabelSuggestions([]);
    }
  };

  const addLabel = async () => {
    const lbl = newLabel.trim();
    if (!lbl) return;
    setLabelBusy(true);
    try {
      await api.post(`${apiPath}/${contact.id}/labels`, { label: lbl });
      setNewLabel("");
      await loadLabels();
      await loadLabelSuggestions();
    } catch {
      /* stille fout */
    } finally {
      setLabelBusy(false);
    }
  };

  const removeLabel = async (lbl: string) => {
    try {
      await api.delete(`${apiPath}/${contact.id}/labels/${encodeURIComponent(lbl)}`);
      await loadLabels();
    } catch {
      /* stille fout */
    }
  };

  useEffect(() => {
    setData(contact);
    setErr("");
    (async () => {
      try {
        const res = await api.get<{ data: Contact }>(`${apiPath}/${contact.id}`);
        setData(res.data.data);
      } catch {
        setErr("Kon de laatste gegevens niet laden; toon wat op de lijst stond.");
      }
    })();
    loadLabels();
    loadLabelSuggestions();
  }, [apiPath, contact.id, contact]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    setContactAuditPanel("loading");
    setContactAuditRows([]);
    void (async () => {
      try {
        const res = await api.get<{ data: ContactAuditTimelineRow[] }>(
          `/api/audit/contact/${contact.id}/timeline`
        );
        if (cancelled) return;
        setContactAuditRows(res.data.data ?? []);
        setContactAuditPanel("ready");
      } catch (e: unknown) {
        if (cancelled) return;
        const st = (e as { response?: { status?: number } })?.response?.status;
        setContactAuditRows([]);
        if (st === 403) setContactAuditPanel("forbidden");
        else setContactAuditPanel("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contact.id]);

  useEffect(() => {
    let cancelled = false;
    setRelations({ status: "loading" });
    void (async () => {
      try {
        const res = await api.get<{
          data: { editable: boolean; outgoing: ContactRelationRow[]; incoming: ContactRelationRow[] };
        }>(`${apiPath}/${contact.id}/relations`);
        if (cancelled) return;
        setRelations({
          status: "ready",
          editable: res.data.data.editable,
          outgoing: res.data.data.outgoing ?? [],
          incoming: res.data.data.incoming ?? [],
        });
      } catch {
        if (cancelled) return;
        setRelations({ status: "hidden" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiPath, contact.id]);

  useEffect(() => {
    let cancelled = false;
    setMlSyncMsg("");
    void api
      .get<{ data: { enabled: boolean; canReview: boolean } }>("/api/mailerlite/status")
      .then((r) => {
        if (!cancelled) setMlStatus(r.data.data);
      })
      .catch(() => {
        if (!cancelled) setMlStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [contact.id]);

  const c = data ?? contact;
  const readOnlyFromServer = !!c._meta?.read_only;
  const deceased = contactIsDeceased(c);
  const em = c.email_prive || c.email;
  const initials = `${c.first_name[0] ?? ""}${c.last_name[0] ?? ""}`.toUpperCase();
  const volleNaam = [c.first_name, c.tussenvoegsel, c.last_name]
    .map((s) => (s || "").toString().trim())
    .filter(Boolean)
    .join(" ");
  const emailLKey = (c.email_lijst as EmailLijstKey) || "geen";
  const lijstenKeys =
    c.email_lijsten && c.email_lijsten.length > 0
      ? c.email_lijsten.filter((k) => k && k !== "geen")
      : emailLKey !== "geen"
        ? [emailLKey]
        : [];
  const lijstenTekst =
    lijstenKeys.length > 0 ? lijstenKeys.map((k) => emailLijstLabel[k] ?? k).join(", ") : emailLijstLabel.geen;

  const mapsQ = formatLocationMapsQuery(c);
  /** Volledige adresregel voor klembord (zelfde als Maps-zoekopdracht) bij Adres / Postcode & plaats / Land. */
  const fullAddressCopy = mapsQ ?? undefined;
  const mapsOnAdresRow = Boolean(c.address?.toString().trim() && mapsQ);
  const mapsOnPostcodeRow = Boolean(!c.address?.toString().trim() && mapsQ);
  const postcodePlaatsLine = [c.postal_code, c.city].filter(Boolean).join(" ") || undefined;

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} type="button" aria-label="Sluiten">
            ✕
          </button>
        </div>
        <div className="modal-body contact-view-modal-body">
          {err && <p className="form-error" style={{ margin: 0 }}>{err}</p>}

          <div className={`contact-view-hero${deceased ? " contact-view-hero--deceased" : ""}`}>
            <div className="contact-avatar" style={{ width: 80, height: 80, fontSize: 22, flexShrink: 0 }}>
              {c.profile_picture ? (
                <img src={`${API_URL}${c.profile_picture}`} alt="" />
              ) : (
                <span>{initials}</span>
              )}
            </div>
            <div>
              <p className="contact-view-naam" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span>{volleNaam || "—"}</span>
                {deceased ? <DeceasedMark withLabel /> : null}
              </p>
              {c.initialen && <p className="contact-view-meta">Initialen: {c.initialen}</p>}
              {c.app_role && c.entity_kind !== "extern" && (
                <span className="group-badge" style={{ marginTop: 6, display: "inline-block" }}>
                  App-rol: {c.app_role}
                </span>
              )}
            </div>
          </div>

          {/* Geschiedenis onder de kop; bij geen rechten (403) hele blok weglaten */}
          {contactAuditPanel !== "forbidden" && (
          <div
            className="contact-view-row contact-view-row--block"
            style={{
              marginTop: 2,
              marginBottom: 8,
              border: "1px solid rgba(44, 33, 133, 0.12)",
              borderRadius: "var(--radius-md, 10px)",
              background: "var(--surface-100, #faf8fc)",
            }}
          >
            <div className="contact-view-lbl" style={{ marginBottom: 6 }}>
              Geschiedenis van deze kaart
            </div>
            {contactAuditPanel === "loading" && (
              <p className="page-subtitle" style={{ margin: 0, fontSize: "0.82rem", color: "var(--text-400)" }}>
                Geschiedenis laden…
              </p>
            )}
            {contactAuditPanel === "error" && (
              <p className="form-error" style={{ margin: 0, fontSize: "0.85rem" }}>
                De geschiedenis kon niet worden geladen (netwerk of server). Probeer het venster te sluiten en opnieuw
                te openen.
              </p>
            )}
            {contactAuditPanel === "ready" && (
              <>
                <p style={{ fontSize: "0.78rem", color: "var(--text-500)", margin: "0 0 10px" }}>
                  Chronologisch overzicht: aanmaak, wijzigingen, groepen, labels, enz. Een <strong>bulk-import</strong>{" "}
                  (Excel) staat vaak als <strong>één</strong> regel bij <strong>Beheer → Activiteiten</strong>, niet
                  voor elke rij apart hier.
                </p>
                {contactAuditRows.length === 0 ? (
                  <p style={{ fontSize: "0.85rem", color: "var(--text-500)", margin: 0 }}>
                    Nog geen regels in het log die aan deze kaart gekoppeld zijn.
                  </p>
                ) : (
                  <ul
                    style={{
                      listStyle: "none",
                      margin: 0,
                      padding: 0,
                      maxHeight: "min(220px, 38vh)",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {contactAuditRows.map((row) => (
                      <li
                        key={row.id}
                        style={{
                          fontSize: "0.84rem",
                          lineHeight: 1.45,
                          paddingBottom: 8,
                          borderBottom: "1px solid var(--surface-300)",
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "var(--text-800)" }}>
                          {formatAuditWhen(row.created_at)}
                        </div>
                        <div style={{ color: "var(--text-800)", marginTop: 2 }}>
                          {summarizeContactAuditRow(row, contact.id)}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-500)", marginTop: 4 }}>
                          {row.user_name || "—"}
                          {row.user_email ? ` · ${row.user_email}` : ""}
                          {row.ip ? ` · ${row.ip}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          )}

          <div className="contact-view-main-grid">
            <div className="contact-view-col-primary">
          <div className="contact-view-dl">
            <Row
              label="E-mail Dominicus"
              copyText={c.dominicus_email ?? undefined}
              value={
                c.dominicus_email ? (
                  <a
                    href={`mailto:${c.dominicus_email}`}
                    className="contact-view-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {c.dominicus_email}
                  </a>
                ) : null
              }
            />
            {em && (
              <Row
                label="E-mail (primair)"
                copyText={em}
                value={
                  <a href={`mailto:${em}`} className="contact-view-link" onClick={(e) => e.stopPropagation()}>
                    {em}
                  </a>
                }
              />
            )}
            <Row
              label="E-mail (secundair)"
              copyText={c.email_werk ?? undefined}
              value={
                c.email_werk ? (
                  <a href={`mailto:${c.email_werk}`} className="contact-view-link" onClick={(e) => e.stopPropagation()}>
                    {c.email_werk}
                  </a>
                ) : null
              }
            />
            <Row label="E-mail lijsten" copyText={lijstenTekst} value={lijstenTekst} />
            <Row
              label="Telefoon (primair)"
              copyText={c.phone ?? undefined}
              value={
                c.phone ? (
                  <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}>
                    {c.phone}
                  </a>
                ) : null
              }
            />
            <Row
              label="Telefoon (secundair)"
              copyText={c.phone_mobile ?? undefined}
              value={
                c.phone_mobile ? (
                  <a href={`tel:${c.phone_mobile}`} onClick={(e) => e.stopPropagation()}>
                    {c.phone_mobile}
                  </a>
                ) : null
              }
            />
            <Row
              label="Adres"
              copyText={fullAddressCopy ?? (c.address?.toString().trim() || undefined)}
              mapsQuery={mapsOnAdresRow ? mapsQ ?? undefined : undefined}
              value={c.address?.toString().trim() ? c.address : null}
            />
            <Row
              label="Postcode & plaats"
              copyText={fullAddressCopy ?? postcodePlaatsLine}
              mapsQuery={mapsOnPostcodeRow ? mapsQ ?? undefined : undefined}
              value={postcodePlaatsLine}
            />
            <Row
              label="Land"
              copyText={fullAddressCopy ?? c.country ?? undefined}
              value={c.country}
              mapsQuery={mapsQ && !mapsOnAdresRow && !mapsOnPostcodeRow ? mapsQ : undefined}
            />
            {(c.entity_kind === "extern" || c.entity_kind === "leverancier") && (
              <Row label="Bedrijfsnaam" copyText={c.bedrijfsnaam ?? undefined} value={c.bedrijfsnaam} />
            )}
            <Row label="Geboortedatum" copyText={c.birth_date ?? undefined} value={c.birth_date} />
            <Row label="Overlijdensdatum" copyText={c.death_date ?? undefined} value={c.death_date} />
            {c.kwalificaties && (
              <div className="contact-view-row contact-view-row--block">
                <div className="contact-view-lbl">Kwalificaties</div>
                <pre className="contact-view-pre">{c.kwalificaties}</pre>
              </div>
            )}
            <Row label="Bellijn" copyText={c.bellijn ? "Ja" : "Nee"} value={c.bellijn ? "Ja" : c.bellijn === false ? "Nee" : undefined} />
            <Row label="Berichten" copyText={c.berichten ? "Ja" : "Nee"} value={c.berichten ? "Ja" : c.berichten === false ? "Nee" : undefined} />
            <Row
              label="AVG / voorwaarden ingestemd"
              copyText={c.agv_ingestemd ? "Ja" : "Nee"}
              value={c.agv_ingestemd ? "Ja" : c.agv_ingestemd === false ? "Nee" : undefined}
            />
            {c.notitie && <Row label="Korte notitie" copyText={c.notitie} value={c.notitie} />}
            {(c.notities || c.notes) && (
              <div className="contact-view-row contact-view-row--block">
                <div className="contact-view-lbl">Notities</div>
                <pre className="contact-view-pre">{c.notities || c.notes}</pre>
              </div>
            )}
            {c.extra && (
              <div className="contact-view-row contact-view-row--block">
                <div className="contact-view-lbl">Extra</div>
                <pre className="contact-view-pre">{c.extra}</pre>
              </div>
            )}
            {c.bezoekersbestand_id != null && (
              <Row label="Bezoekersbestand" copyText={`#${c.bezoekersbestand_id}`} value={`#${c.bezoekersbestand_id}`} />
            )}
          </div>
            </div>

            <aside className="contact-view-col-sidebar" aria-label="Groepen, lijsten en relaties">
              <div className="contact-view-sidebar-card">
                <h3 className="contact-view-sidebar-title">Groepen</h3>
                {c.groups && c.groups.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {c.groups.map((g) => (
                      <span key={g.id} className="group-badge">
                        {g.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--text-400)", lineHeight: 1.45 }}>
                    Niet gekoppeld aan een groep.
                  </p>
                )}
              </div>

          {/* ── Lijsten: links titel + uitleg, rechts de vinkjes (zelfde grid als andere rijen) ── */}
          <div className="contact-view-sidebar-card">
          <div className="contact-view-row" style={{ alignItems: "start" }}>
            <div style={{ minWidth: 0 }}>
              <div className="contact-view-lbl" style={{ marginBottom: 6, paddingTop: 6 }}>
                In welke lijsten?
              </div>
              <p
                style={{
                  fontSize: "0.78rem",
                  color: "var(--text-400)",
                  margin: 0,
                  lineHeight: 1.45,
                  textTransform: "none",
                  fontWeight: 400,
                  letterSpacing: "normal",
                }}
              >
                {readOnlyFromServer ? (
                  <>Alleen bekijken: lijst-koppelingen kun je hier niet wijzigen.</>
                ) : (
                  <>
                    Vink extra lijsten aan om dit contact op meerdere lijsten te laten verschijnen.
                    De primaire lijst stel je in via <em>Bewerken</em>.
                  </>
                )}
              </p>
            </div>
            <div className="contact-view-val" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {OFFICIËLE_LIJSTEN.map(({ value, label }) => {
                const isPrimair = c.entity_kind === value;
                const hasLabel = labels.includes(value);
                const isChecked = isPrimair || hasLabel;
                if (readOnlyFromServer) {
                  if (!isChecked) return null;
                  return (
                    <div key={value} style={{ fontSize: "0.9rem" }}>
                      {label}
                      {isPrimair && (
                        <span style={{ fontSize: "0.75rem", color: "var(--text-400)", marginLeft: 6 }}>
                          (primaire lijst)
                        </span>
                      )}
                    </div>
                  );
                }
                return (
                  <label
                    key={value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: isPrimair ? "default" : "pointer",
                      fontSize: "0.9rem",
                    }}
                    title={isPrimair ? "Dit is de primaire lijst – wijzig via Bewerken" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={labelBusy || isPrimair}
                      onChange={async () => {
                        setLabelBusy(true);
                        try {
                          if (hasLabel) {
                            await api.delete(`${apiPath}/${c.id}/labels/${encodeURIComponent(value)}`);
                          } else {
                            await api.post(`${apiPath}/${c.id}/labels`, { label: value });
                          }
                          await loadLabels();
                        } finally {
                          setLabelBusy(false);
                        }
                      }}
                    />
                    <span>{label}</span>
                    {isPrimair && (
                      <span style={{ fontSize: "0.75rem", color: "var(--text-400)", marginLeft: 2 }}>
                        (primaire lijst)
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
          </div>

          {/* ── Vrije tags (label links, invoer bovenaan rechts, badges daaronder) ── */}
          <div className="contact-view-sidebar-card">
          <div className="contact-view-row" style={{ alignItems: "start" }}>
            <div className="contact-view-lbl" style={{ paddingTop: 6 }}>
              Vrije tags
            </div>
            <div className="contact-view-val" style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
              {!readOnlyFromServer && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <datalist id={`free-tag-suggestions-${contact.id}`}>
                  {labelSuggestions.map((lbl) => (
                    <option key={lbl} value={lbl} />
                  ))}
                </datalist>
                <input
                  ref={newLabelRef}
                  type="text"
                  placeholder="Nieuwe tag of bestaande kiezen…"
                  list={`free-tag-suggestions-${contact.id}`}
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addLabel();
                    }
                  }}
                  maxLength={80}
                  style={{
                    fontSize: "0.875rem",
                    padding: "0.35rem 0.6rem",
                    border: "1px solid var(--surface-300)",
                    borderRadius: 6,
                    flex: "1 1 160px",
                    minWidth: 0,
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: "0.8rem", padding: "0.35rem 0.75rem" }}
                  disabled={labelBusy || !newLabel.trim()}
                  onClick={addLabel}
                >
                  + Toevoegen
                </button>
              </div>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {labels.filter((lbl) => !OFFICIËLE_LIJSTEN.some((o) => o.value === lbl)).length === 0 && (
                  <span style={{ fontSize: "0.85rem", color: "var(--text-400)" }}>Geen tags toegevoegd</span>
                )}
                {labels
                  .filter((lbl) => !OFFICIËLE_LIJSTEN.some((o) => o.value === lbl))
                  .map((lbl) => (
                    <span
                      key={lbl}
                      className="group-badge"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      {lbl}
                      {!readOnlyFromServer && (
                        <button
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "0 2px",
                            lineHeight: 1,
                            color: "inherit",
                            opacity: 0.7,
                          }}
                          title={`Verwijder tag "${lbl}"`}
                          onClick={() => removeLabel(lbl)}
                          aria-label={`Verwijder tag ${lbl}`}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  ))}
              </div>
            </div>
          </div>
          </div>

          {/* ── Relaties ─────────────────────────────────────────────── */}
          {relations.status === "ready" && (
            <div
              ref={relationsSectionRef}
              id="contact-view-relations-card"
              className="contact-view-sidebar-card"
            >
            <div className="contact-view-row" style={{ alignItems: "start" }}>
              <div style={{ minWidth: 0 }}>
                <div className="contact-view-lbl" style={{ marginBottom: 6, paddingTop: 6 }}>
                  Relaties
                </div>
                <p
                  style={{
                    fontSize: "0.78rem",
                    color: "var(--text-400)",
                    margin: 0,
                    lineHeight: 1.45,
                    textTransform: "none",
                    fontWeight: 400,
                    letterSpacing: "normal",
                  }}
                >
                  <strong>Uitgaand:</strong> de gekozen persoon is jouw moeder, partner, enz.{" "}
                  <strong>Inkomend:</strong> op een andere kaart staat een band met jou (bijv. “jij bent hun vader”).{" "}
                  Bij <strong>partner</strong> kun je de status wijzigen naar <strong>gescheiden</strong> of{" "}
                  <strong>partner overleden</strong> (geldt voor beide kaarten).
                </p>
              </div>
              <div className="contact-view-val" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {relations.outgoing.length === 0 && relations.incoming.length === 0 && !relations.editable && (
                  <span style={{ fontSize: "0.85rem", color: "var(--text-400)" }}>Geen relaties vastgelegd.</span>
                )}
                {relations.outgoing.map((r) => (
                  <div
                    key={`o-${r.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "0.9rem" }}
                  >
                    <span>
                      <strong>{CONTACT_RELATION_LABELS_NL[r.relation_code]}</strong>:{" "}
                      <button
                        type="button"
                        className="contact-view-relation-name-jump"
                        title="Ga naar het blok Relaties"
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToRelationsSection();
                        }}
                      >
                        {[r.other_first_name, r.other_last_name].filter(Boolean).join(" ") || "—"}
                      </button>
                    </span>
                    {relations.editable && isPartnerLifecycleRelationCode(r.relation_code) && (
                      <select
                        className="search-input"
                        title="Status van deze partnerkoppeling"
                        style={{ fontSize: "0.8rem", padding: "4px 8px", maxWidth: 260 }}
                        value={r.relation_code}
                        disabled={relationPatchBusy}
                        onChange={async (e) => {
                          const next = e.target.value as PartnerLifecycleRelationCode;
                          if (next === r.relation_code) return;
                          setRelationPatchBusy(true);
                          try {
                            await api.patch(`${apiPath}/${contact.id}/relations/${r.id}`, { relation_code: next });
                            await reloadRelations();
                          } catch {
                            /* stille fout */
                          } finally {
                            setRelationPatchBusy(false);
                          }
                        }}
                      >
                        {PARTNER_LIFECYCLE_CODES.map((code) => (
                          <option key={code} value={code}>
                            {CONTACT_RELATION_LABELS_NL[code]}
                          </option>
                        ))}
                      </select>
                    )}
                    {relations.editable && (
                      <button
                        type="button"
                        className="btn-danger-sm"
                        title="Relatie verwijderen"
                        onClick={async () => {
                          try {
                            await api.delete(`${apiPath}/${contact.id}/relations/${r.id}`);
                            await reloadRelations();
                          } catch {
                            /* stille fout */
                          }
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {relations.incoming.map((r) => (
                  <div
                    key={`i-${r.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "0.9rem" }}
                  >
                    <span>
                      <strong>{CONTACT_RELATION_LABELS_NL[r.relation_code]}</strong>:{" "}
                      <button
                        type="button"
                        className="contact-view-relation-name-jump"
                        title="Ga naar het blok Relaties"
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToRelationsSection();
                        }}
                      >
                        {[r.other_first_name, r.other_last_name].filter(Boolean).join(" ") || "—"}
                      </button>
                    </span>
                    {relations.editable && isPartnerLifecycleRelationCode(r.relation_code) && (
                      <select
                        className="search-input"
                        title="Status van deze partnerkoppeling"
                        style={{ fontSize: "0.8rem", padding: "4px 8px", maxWidth: 260 }}
                        value={r.relation_code}
                        disabled={relationPatchBusy}
                        onChange={async (e) => {
                          const next = e.target.value as PartnerLifecycleRelationCode;
                          if (next === r.relation_code) return;
                          setRelationPatchBusy(true);
                          try {
                            await api.patch(`${apiPath}/${contact.id}/relations/${r.id}`, { relation_code: next });
                            await reloadRelations();
                          } catch {
                            /* stille fout */
                          } finally {
                            setRelationPatchBusy(false);
                          }
                        }}
                      >
                        {PARTNER_LIFECYCLE_CODES.map((code) => (
                          <option key={code} value={code}>
                            {CONTACT_RELATION_LABELS_NL[code]}
                          </option>
                        ))}
                      </select>
                    )}
                    {relations.editable && (
                      <button
                        type="button"
                        className="btn-danger-sm"
                        title="Relatie verwijderen"
                        onClick={async () => {
                          try {
                            await api.delete(`${apiPath}/${contact.id}/relations/${r.id}`);
                            await reloadRelations();
                          } catch {
                            /* stille fout */
                          }
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {relations.editable && (
                  <button type="button" className="btn-secondary" style={{ alignSelf: "flex-start", fontSize: "0.82rem" }} onClick={() => setRelationModalOpen(true)}>
                    + Relatie toevoegen
                  </button>
                )}
              </div>
            </div>
            </div>
          )}
            </aside>
          </div>

          <div className="modal-footer" style={{ marginTop: 8, paddingTop: 16, borderTop: "1px solid var(--surface-200)" }}>
            {mlSyncMsg ? (
              <p className="form-hint" style={{ width: "100%", marginBottom: 8 }}>
                {mlSyncMsg}
              </p>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", width: "100%" }}>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Sluiten
              </button>
              {mlStatus?.enabled && (em || "").trim() && !deceased && !readOnlyFromServer ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={mlSyncBusy}
                  title="Stuur deze kaart naar MailerLite (lijsten + naam/e-mail/telefoon)"
                  onClick={async () => {
                    setMlSyncBusy(true);
                    setMlSyncMsg("");
                    try {
                      await api.post(`/api/mailerlite/sync/contact/${c.id}`, {});
                      setMlSyncMsg("Gesynchroniseerd met MailerLite.");
                    } catch (e: unknown) {
                      const ax = e as { response?: { data?: { error?: string } } };
                      setMlSyncMsg(ax.response?.data?.error || "Synchroniseren mislukt.");
                    } finally {
                      setMlSyncBusy(false);
                    }
                  }}
                >
                  {mlSyncBusy ? "Bezig…" : "Sync naar MailerLite"}
                </button>
              ) : null}
              {showEditButton && onEdit && canDuplicateContactCard(user) && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={duplicateBusy}
                  title="Maakt een kopie met dezelfde gegevens en groepen; daarna opent het bewerkvenster voor de nieuwe kaart."
                  onClick={async () => {
                    setDuplicateBusy(true);
                    setErr("");
                    try {
                      const res = await api.post<{ data: Contact }>(`${apiPath}/${c.id}/duplicate`, {});
                      onEdit(res.data.data);
                      onClose();
                    } catch {
                      setErr("Dupliceren mislukt. Controleer je rechten en probeer opnieuw.");
                    } finally {
                      setDuplicateBusy(false);
                    }
                  }}
                >
                  {duplicateBusy ? "Bezig…" : "Dupliceren en bewerken"}
                </button>
              )}
              {showEditButton && onEdit && (
                <button type="button" className="btn-primary" onClick={() => { onEdit(c); onClose(); }}>
                  Bewerken
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <ContactRelationAddModal
      open={relationModalOpen}
      onClose={() => setRelationModalOpen(false)}
      contactId={contact.id}
      apiPath={apiPath}
      onAdded={reloadRelations}
    />
    </>
  );
}
